import { atomFamily, selectorFamily, useRecoilState } from "recoil";
import moment from "moment";
import filter from "lodash/filter";
import flatMap from "lodash/flatMap";
import reject from "lodash/reject";
import produce from "immer";
import { getProviderGameTemplates } from "api/GameTemplatesApi";
import * as ProviderApi from "api/ProviderApi";
import { mapGameDataFromServer } from "data/serverMapping";

const loadProviderGameTemplateList = selectorFamily({
    key: "LoadProviderGameTemplateList",
    get: (filters) => async () => {
        const personalTemplates = await getProviderGameTemplates(filters);
        return filter(flatMap(personalTemplates.body?.result), { isTemplate: true });
    },
});

export const providerGameTemplateListState = atomFamily({
    key: "ProviderGameTemplateListState",
    default: loadProviderGameTemplateList,
});

export function useProviderGameTemplateListMutations(filters) {
    const [providerGameTemplateList, setProviderGameTemplateList] = useRecoilState(providerGameTemplateListState(filters));

    const createTemplate = async (template) => {
        await ProviderApi.saveGame(template, filters.userId);
        // reload templates after create
        const res = await getProviderGameTemplates(filters);
        setProviderGameTemplateList(filter(flatMap(res.body?.result), { isTemplate: true }));
    };

    const deleteTemplate = async (templateId) => {
        await ProviderApi.deleteGame(templateId, filters.userId);
        setProviderGameTemplateList((prev) => produce(prev, draft => {
            return reject(draft, { greenlineGameId: templateId });
        }));
    };

    const duplicateTemplate = async (template) => {
        const res = await ProviderApi.getGameById(template.greenlineGameId, filters.userId, false)
        const data = mapGameDataFromServer(res.body.result);
        const newData = {
            ...data,
            startDate: new moment().add(1, "day").format("MM/DD/YYYY"),
            isTemplate: true,
            copiedFromGreenlineGameId: data.greenlineGameId,
            greenlineGameId: 0,
            name: `${data.name} (COPY)`,
            originalStartDate: new moment().format("MM/DD/YYYY"),
        };
        await ProviderApi.saveGame(newData, filters.userId);
        const personalTemplates = await getProviderGameTemplates(filters, false);
        setProviderGameTemplateList(filter(flatMap(personalTemplates.body?.result), { isTemplate: true }));
    };

    const updateTemplate = async (template) => {
        await ProviderApi.saveGame(template, filters.userId);
        // reload templates after update
        const res = await getProviderGameTemplates(filters);
        setProviderGameTemplateList(filter(flatMap(res.body?.result), { isTemplate: true }));
    };

    return {
        createTemplate,
        deleteTemplate,
        duplicateTemplate,
        updateTemplate,
    };
}